import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 🔲 CATIA-Style Datum Feature (Referans Özellik)
 * Datum reference marker for GD&T annotations
 */

export class FikoDatumOzellik extends THREE.Group {
    override type = 'FikoDatumOzellik';

    // 🔴 SABİT BOYUT
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private pozisyon: THREE.Vector3;
    private datumHarfi: string;
    private renk: string = '#00BCD4';

    private sprite: THREE.Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        pozisyon: THREE.Vector3,
        datumHarfi: string = 'A'
    ) {
        super();

        this.pozisyon = pozisyon.clone();
        this.datumHarfi = datumHarfi.toUpperCase();

        this.canvas = document.createElement('canvas');
        this.canvas.width = 64;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = `Datum ${this.datumHarfi}`;
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { /* Ignored */ }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;
        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.sprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.sprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createSprite();
    }

    private createSprite(): void {
        const ctx = this.context;
        const size = 60; // Square datum frame

        this.canvas.width = size;
        this.canvas.height = size;
        ctx.clearRect(0, 0, size, size);

        // Draw datum frame (square with line on one side)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.fillRect(5, 5, size - 10, size - 10);

        ctx.strokeStyle = this.renk;
        ctx.lineWidth = 3;
        ctx.strokeRect(5, 5, size - 10, size - 10);

        // Datum reference line (left side)
        ctx.beginPath();
        ctx.moveTo(5, 5);
        ctx.lineTo(0, 0);
        ctx.lineTo(0, size);
        ctx.lineTo(5, size - 5);
        ctx.stroke();

        // Datum letter
        ctx.font = 'bold 28px Arial';
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.datumHarfi, size / 2, size / 2);

        this.texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.sprite = new THREE.Sprite(spriteMaterial);
        this.sprite.position.copy(this.pozisyon);
        this.sprite.scale.set(1, 1, 1);
        this.add(this.sprite);
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.sprite) {
            this.sprite.material.map?.dispose();
            this.sprite.material.dispose();
        }
        this.texture.dispose();
    }
}
